import { useCallback, useMemo, useRef, useState } from 'react'
import { ArrowRight } from 'lucide-react'
import {
  LANDING_DEMO_DEFAULT_WORD,
  LANDING_DEMO_PARAGRAPH,
  landingDemoResponseFor,
} from '@/lib/landingDemoFixtures'
import { tokenizeContent } from '@/lib/tokenizeContent'
import { LandingDemoSidePanel } from '@/components/landing/LandingDemoPanel'
import { useLandingCopy, useGuestLocale } from '@/hooks/useGuestLocale'
import { landingDemoShellClass } from '@/components/landing/landingStyles'
import { cn } from '@/components/ui/cn'

interface LandingDemoReaderProps {
  onLookup: (word: string) => void
}

export function LandingDemoReader({ onLookup }: LandingDemoReaderProps) {
  const copy = useLandingCopy()
  const locale = useGuestLocale()
  const contentRef = useRef<HTMLParagraphElement>(null)
  const [selection, setSelection] = useState(LANDING_DEMO_DEFAULT_WORD)
  const [hasUserTapped, setHasUserTapped] = useState(false)

  const tokens = useMemo(() => tokenizeContent(LANDING_DEMO_PARAGRAPH), [])

  const activeUnderstand = useMemo(
    () => landingDemoResponseFor(selection, locale),
    [selection, locale],
  )

  const selectWord = useCallback(
    (word: string, fromUser: boolean) => {
      if (!landingDemoResponseFor(word, locale)) return
      setSelection(word)
      if (fromUser) {
        setHasUserTapped(true)
        onLookup(word)
      }
    },
    [locale, onLookup],
  )

  const handleWordClick = useCallback(
    (word: string) => selectWord(word, true),
    [selectWord],
  )

  return (
    <div
      id="landing-demo-reader"
      className={cn(landingDemoShellClass, 'overflow-hidden')}
      data-testid="landing-demo-reader"
    >
      <div className="grid border-b border-slate-900/[0.06] bg-slate-50/50 px-5 py-2.5 text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground sm:px-6 lg:grid-cols-2">
        <span>{copy.demoReadLabel}</span>
        <span className="mt-1 flex items-center gap-1.5 lg:mt-0">
          <ArrowRight className="h-3.5 w-3.5 text-primary lg:hidden" aria-hidden />
          {copy.demoUnderstandLabel}
        </span>
      </div>

      <div className="grid lg:grid-cols-2">
        <div className="border-b border-slate-900/[0.06] p-6 sm:p-7 lg:border-b-0 lg:border-r lg:border-slate-900/[0.06]">
          <p
            ref={contentRef}
            className="text-[1.05rem] leading-[1.75] text-foreground sm:text-lg sm:leading-[1.8]"
          >
            {tokens.map((token, i) => {
              if (!token.isWord) {
                return <span key={i}>{token.text}</span>
              }

              const hasDemo = Boolean(landingDemoResponseFor(token.lower, locale))
              const isActive = selection === token.lower
              const isSuggested = hasDemo && token.lower === LANDING_DEMO_DEFAULT_WORD && !hasUserTapped

              return (
                <span
                  key={i}
                  data-demo-word={hasDemo ? token.lower : undefined}
                  onClick={hasDemo ? () => handleWordClick(token.lower) : undefined}
                  data-word-clickable={hasDemo ? 'true' : undefined}
                  className={cn(
                    hasDemo &&
                      'cursor-pointer rounded px-0.5 font-medium text-primary underline decoration-primary/60 decoration-2 underline-offset-[4px] transition-all hover:bg-primary/15',
                    isActive && 'bg-primary/20 decoration-primary decoration-solid',
                    isSuggested && 'ring-2 ring-primary/40 ring-offset-2 ring-offset-card',
                  )}
                >
                  {token.text}
                </span>
              )
            })}
          </p>
        </div>

        <div className="bg-slate-50/30 p-6 sm:p-7">
          <LandingDemoSidePanel
            understand={activeUnderstand}
            isPreview={!hasUserTapped}
          />
        </div>
      </div>
    </div>
  )
}
